import type { Auth, User } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as api from "./api";
import type { AuthError } from "firebase/auth";
import {
  authFor,
  dropPending,
  finishRedirect,
  keepPending,
  linkPending,
  prepareProvider,
  type Provider,
  type RedirectOutcome,
  rememberEmail,
  savedEmail,
  signInWithProvider,
  signInWithToken,
} from "./auth";
import { AccountList, AccountPage } from "./AccountPages";
import { IssueList } from "./IssuePages";
import { LeadList, LeadPage } from "./LeadPages";
import { FollowUpList } from "./FollowUpPages";
import { RenewalList } from "./ContractPages";
import { PayoutSettings, RebateList, RebateRun } from "./RebatePages";
import { AgreementPage, DealList, DealPage } from "./DealPages";
import { PreTripSettings } from "./PreTripSettings";
import { VisitLabelSettings } from "./VisitLabels";
import { PlannerSettings } from "./PlannerSettings";
import { SalesAreaSettings } from "./SalesAreaSettings";
import { SalesSettings } from "./SalesSettings";
import { ForecastSettings } from "./ForecastSettings";
import { ContainerSettings } from "./ContainerSettings";
import { ContainerSizes } from "./ContainerSizes";
import { SizeSavings } from "./SizeSavings";
import { SensorRuleSettings } from "./SensorRuleSettings";
import { CyclePage } from "./CyclePage";
import { WorkOrderList } from "./WorkOrderPages";
import { OverviewPage } from "./OverviewPage";
import { PickupsCharts, PickupsPage } from "./PickupsPage";
import { localNow } from "./visits";
import { ReviewPage } from "./ReviewPage";
import { RoutesPage } from "./RoutesPage";
import { TodayPage } from "./TodayPage";
import { VisitPage } from "./VisitPage";
import { VisitsPage } from "./VisitsPage";
import { ParityPage } from "./ParityPage";
import { RequestsPage } from "./RequestsPage";
import { MessagesPage } from "./MessagesPage";
import { FillViewPage } from "./FillViewPage";
import { PortalPage } from "./PortalPage";
import { AccountMenu } from "./AccountMenu";
import { OrgSettings, OrgSwitcher } from "./OrgSwitch";
import { MenuSettings, useMenuAll } from "./MenuSettings";
import { NotificationSettings } from "./NotificationSettings";
import { IssueReminderSettings } from "./IssueReminderSettings";
import { MessageMailSettings } from "./MessageMailSettings";
import { PlaceCheckSettings } from "./PlaceCheckSettings";
import { PortalContactSettings } from "./PortalContactSettings";
import { TelegramAlertSettings } from "./TelegramAlertSettings";
import { ScorecardPage } from "./ScorecardPage";
import { CalendarSettings } from "./CalendarSettings";
import { HolidaySettings } from "./HolidaySettings";
import { FleetSettings } from "./FleetSettings";
import { LoadList } from "./LoadPages";
import { KeyList } from "./KeyPages";
import { ActivityPage, ExportsPage } from "./ExportPages";
import { CutoverPage } from "./CutoverPage";
import { ImportPage } from "./ImportPages";
import { RoleList, RolePage } from "./RolePages";
import { renamedModules } from "./roles";
import { UnitList, UnitPage } from "./UnitPages";
import { UserList, UserPage } from "./UserPages";
import { TankPanel, TestChip } from "./TankPanel";
import { age, attention, battery, byAttention, clockTime, isStale, level, lowBattery, readingTime } from "./format";
import { FormSection } from "./FormSection";
import { go, useClock, useCrumb, useCrumbLabel, useSection, useTitle } from "./hooks";
import { isId, type Page, route } from "./route";
import { readVisits, remember, type Visit } from "./jump";
import { TANK_COLUMNS } from "./columns";
import { STATUS_TEXT } from "./accounts";
import {
  CONTAINER_COLUMNS,
  type ContainerRow,
  containerSummary,
  detailParts,
  filterRows,
  filterTanks,
  fullestFirst,
  isTankFiltered,
  lastServiceText,
  levelOf,
  NO_TANK_FILTER,
  nextVisitText,
  readTankFilter,
  kindText,
  rowsOf,
  rowTanks,
  sizeText,
  SOURCE_TEXT,
  type TankFilter,
  unlinkedCount,
  unlinkedText,
  weeksLeftText,
  writeTankFilter,
} from "./containers";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { ShareButton } from "./Share";
import { AdminSlot, Breadcrumbs, Drawer, HeaderSearch, Jump, MenuIcon, refocus, SideMenu, store, stored, TabHead, TabsContext, typing, useHash, useWide, WrenchIcon } from "./Menu";
import { adminPath, ancestors, currentId, homePage, isExact, menuFor, movedSection, pageAllowed, pages, pathOf, setPin, togglePin, visible } from "./nav";
import {
  connectedText,
  connectFailedText,
  connectMissedText,
  connectText,
  emailDomain,
  errorCode,
  forgetOrg,
  lastOrg,
  linkCode,
  linkConnect,
  linkErrorText,
  MAX_EMAIL,
  NOT_VALID,
  primaryFor,
  providerDetail,
  providerError,
  redirectLostText,
  redeemError,
  rememberOrg,
  sendError,
  sentText,
  startSentText,
  tokenUid,
  withTimeout,
} from "./start";
import { applyTheme, currentTheme, type Theme, watchTheme } from "./theme";
import { cachedFetch, dayKeys, type Found, isNetworkError, loadBootstrap, offlineWindowHours, signInDue } from "./offline/cache";
import { userScope } from "./offline/db";
import { checkAccess, checkRoles, clearMemory, openScope } from "./store/store";
import { CONTAINERS } from "./store/pageViews";
import { failedText, keptText } from "./store/accounts";
import { useView } from "./store/useView";
import { RefreshFailed, refreshFailure } from "./RefreshFailed";
import { startSync } from "./store/sync";
import { warmViews } from "./store/warmup";
import { refreshDay, useOnline } from "./offline/day";
import { useOutbox, useOutboxSender } from "./offline/useOutbox";
import { waiting } from "./offline/outbox";
import { SyncBadge, SyncPage } from "./SyncPage";
import { setMemberNames } from "./memberNames";
import { InstallButton } from "./offline/Install";
import { useVersionText, versionTitle } from "./offline/update";
import { clientLog } from "./clientLog";
import { ConeBar } from "./ConeCard";
import { ConeMember } from "./cone";
import { Hint } from "./Hint";
import { Skeleton, SkeletonBar, SkeletonTools } from "./Skeleton";
import { dropShell, keepShell, keptMay, type KeptShell, readShell } from "./shell";

function usePath(): string {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const update = () => setPath(location.pathname);
    addEventListener("popstate", update);
    return () => removeEventListener("popstate", update);
  }, []);
  return path;
}

function signOutHome(auth: Auth) {
  history.replaceState(null, "", location.pathname.split("/").slice(0, 3).join("/"));
  dispatchEvent(new PopStateEvent("popstate"));
  return signOut(auth);
}

export function App() {
  const target = route(usePath());
  const visits = useLinkVisits();
  if (target.kind === "picker") return <Start />;
  if (target.kind === "portal") return <PortalPage tab={target.tab} />;
  if (target.kind === "redirect") {
    location.replace(`${target.to}${location.search}`);
    return null;
  }
  return <Org key={visits} slug={target.slug} page={target.page} id={target.id} />;
}

function useLinkVisits(): number {
  const [visits, setVisits] = useState(0);
  useEffect(() => {
    const update = () => {
      if (linkCode() !== null) setVisits((n) => n + 1);
    };
    addEventListener("hashchange", update);
    return () => removeEventListener("hashchange", update);
  }, []);
  return visits;
}

function Start() {
  const [saved] = useState(lastOrg);
  const [email, setEmail] = useState(savedEmail() ?? "");
  const [found, setFound] = useState<api.Discovery | null>(null);
  const [byName, setByName] = useState(false);
  const [slug, setSlug] = useState("");
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const slugField = useRef<HTMLInputElement>(null);
  const typed = useRef(email);
  useTitle("Sign in");
  useEffect(() => {
    if (saved) location.replace(`/o/${encodeURIComponent(saved)}`);
  }, [saved]);
  useEffect(() => {
    if (byName) slugField.current?.focus();
  }, [byName]);
  if (saved) return <main className="wide" />;

  const open = (target: string) => location.assign(`/o/${encodeURIComponent(target)}`);
  const look = async (): Promise<api.Discovery | null> => {
    const domain = emailDomain(email);
    if (!domain) return null;
    const result = await api.discover(domain);
    if (emailDomain(typed.current) !== domain) return null;
    setFound(result);
    return result;
  };
  const next = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSent("");
    if (!emailDomain(email)) {
      setError("Enter your work email address, for example name@your-company.com.");
      return;
    }
    const address = email.trim();
    if (address.length > MAX_EMAIL) {
      setError("Enter a shorter email address.");
      return;
    }
    rememberEmail(address);
    setBusy(true);
    try {
      const result = await look();
      if (result?.slug) {
        rememberEmail(address, result.slug);
        open(result.slug);
        return;
      }
      await api.signinLink(address);
      setSent(address);
    } catch (e) {
      setError(sendError(e));
    } finally {
      setBusy(false);
    }
  };
  const openSlug = (event: FormEvent) => {
    event.preventDefault();
    const target = slug.trim().toLowerCase();
    if (target) open(target);
  };
  const described = [error && "start-error", found?.name && "start-org"].filter(Boolean).join(" ");
  return (
    <main className="narrow">
      <h1>Sign in to Xflow</h1>
      <form onSubmit={next} noValidate>
        <label htmlFor="start-email">Work email address</label>
        <input
          id="start-email"
          data-testid="start-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            typed.current = e.target.value;
            setError("");
            setFound(null);
            setSent("");
          }}
          onBlur={() => look().catch(() => undefined)}
          aria-invalid={error ? true : undefined}
          aria-describedby={described || undefined}
        />
        {found?.name && (
          <p id="start-org" role="status" className="found" data-testid="start-org">
            ✓ {found.name}
          </p>
        )}
        {error && (
          <p id="start-error" role="alert" className="error">
            {error}
          </p>
        )}
        <button className="primary" data-testid="start-next" disabled={busy}>
          {busy ? "Checking…" : "Next"}
        </button>
      </form>
      {sent && (
        <p role="status" data-testid="link-sent">
          {startSentText(sent)}
        </p>
      )}
      {byName ? (
        <form onSubmit={openSlug} data-testid="org-fallback">
          <label htmlFor="org-slug">Organization short name</label>
          <input
            id="org-slug"
            ref={slugField}
            data-testid="org-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            aria-describedby="org-hint"
            autoCapitalize="none"
            autoCorrect="off"
          />
          <p id="org-hint" className="hint">
            Your administrator can give it to you.
          </p>
          <button data-testid="open-org">Open organization</button>
        </form>
      ) : (
        <p>
          <button type="button" className="quiet" data-testid="show-org-slug" onClick={() => setByName(true)}>
            Open an organization by its short name
          </button>
        </p>
      )}
      <p className="sub" data-testid="start-portal-link">
        Are you a restaurant? <a href="/portal">Sign in to Xflow Customer</a>
      </p>
    </main>
  );
}

function Org({ slug, page, id }: { slug: string; page: Page; id?: string }) {
  const [code] = useState(linkCode);
  const kept = useMemo(() => (code === null ? readShell(slug) : null), [code, slug]);
  const [org, setOrg] = useState<api.Bootstrap | "missing" | "unreachable" | undefined>(kept?.org);
  const [auth, setAuth] = useState<Auth | null>(() => (kept ? authFor(slug, kept.org) : null));
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const openedSlug = useRef(slug);
  const [connect] = useState(linkConnect);
  const [link, setLink] = useState<Link>(() =>
    code === null ? { kind: "none" } : code ? { kind: "redeeming" } : { kind: "failed", error: NOT_VALID },
  );
  const started = useRef(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  useEffect(() => {
    if (code !== null) history.replaceState(null, "", `${location.pathname}${location.search}`);
  }, [code]);

  useEffect(() => {
    let current = true;
    if (openedSlug.current !== slug) {
      openedSlug.current = slug;
      setOrg(kept?.org);
      setAuth(kept ? authFor(slug, kept.org) : null);
    } else {
      setOrg((now) => (typeof now === "object" ? now : undefined));
    }
    const failed = (e: unknown) => {
      if (!current) return;
      const missing = e instanceof api.ApiError && e.status === 404;
      if (missing) {
        forgetOrg();
        dropShell(slug);
      }
      setOrg((now) => (missing ? "missing" : typeof now === "object" ? now : "unreachable"));
    };
    const show = (value: api.Bootstrap) => {
      setOrg((now) => (JSON.stringify(now) === JSON.stringify(value) ? now : value));
      setAuth(authFor(slug, value));
    };
    loadBootstrap(slug, () => api.bootstrap(slug))
      .then(({ value: found, fresh }) => {
        if (!current) return;
        show(found);
        fresh?.then(({ value }) => current && show(value), (e) => !isNetworkError(e) && failed(e));
      })
      .catch(failed);
    return () => {
      current = false;
    };
  }, [slug, attempt, kept]);

  useEffect(
    () =>
      auth
        ? onAuthStateChanged(auth, (next) => {
            if (!next) {
              clearMemory();
              dropShell(slug);
            }
            setUser(next);
          })
        : undefined,
    [auth, slug],
  );
  useEffect(() => clientLog.signIn(user ? () => user.getIdToken() : null), [user]);

  const [redirected, setRedirected] = useState<RedirectOutcome | null>(null);
  useEffect(() => {
    if (!auth) return;
    let current = true;
    finishRedirect(auth, slug).then((outcome) => current && setRedirected(outcome));
    return () => {
      current = false;
    };
  }, [auth, slug]);

  useEffect(() => {
    if (user === null) setNotice(null);
  }, [user]);

  useEffect(() => {
    if (!code || !auth || user === undefined || started.current) return;
    started.current = true;
    withTimeout(api.redeem(slug, code))
      .then(async ({ custom_token }) => {
        if (user && user.uid !== tokenUid(custom_token)) {
          setLink({ kind: "other", token: custom_token });
          return;
        }
        if (!user) {
          await withTimeout(signInWithToken(auth, custom_token));
          await linkPending(auth, slug).then(
            (name) => {
              if (name) setNotice({ ok: true, text: connectedText(labelOf(name)) });
              else if (connect) setNotice({ ok: false, keep: true, text: connectMissedText(labelOf(connect)) });
            },
            (e: { provider?: Provider }) =>
              setNotice({ ok: false, keep: true, text: connectFailedText(labelOf(e.provider)) }),
          );
        }
        setLink({ kind: "none" });
      })
      .catch((e) => setLink(user ? { kind: "none" } : { kind: "failed", error: redeemError(e) }));
  }, [code, connect, slug, auth, user]);

  if (org === "missing") {
    return (
      <main className="narrow" data-testid="org-missing">
        <h1>Organization not found</h1>
        <p>
          No organization has the short name “{slug}”. Check the address, or{" "}
          <a href="/">sign in with your email address</a>.
        </p>
      </main>
    );
  }
  if (org === "unreachable") {
    return (
      <main className="narrow" data-testid="org-unreachable">
        <h1>Cannot reach Xflow</h1>
        <p>Check your connection, then try again. If it still fails, try again in a few minutes.</p>
        <button className="primary" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </button>
      </main>
    );
  }
  if (org === undefined || auth === null || user === undefined) {
    return <BootShell slug={slug} page={page} name={org?.name} kept={kept} />;
  }
  if (link.kind === "redeeming") return <main className="narrow">Signing you in…</main>;
  if (user === null) {
    return (
      <SignIn
        slug={slug}
        org={org}
        auth={auth}
        linkError={link.kind === "failed" ? link.error : ""}
        redirected={redirected}
      />
    );
  }
  if (link.kind === "other") {
    const current = user.email ?? "another account";
    const useLink = () => {
      setLink({ kind: "redeeming" });
      signOut(auth)
        .then(() => withTimeout(signInWithToken(auth, link.token)))
        .then(() => setLink({ kind: "none" }))
        .catch((e) => setLink({ kind: "failed", error: redeemError(e) }));
    };
    return (
      <OtherAccount name={org.name} current={current} onUse={useLink} onStay={() => setLink({ kind: "none" })} />
    );
  }
  return (
    <ConeMember user={user} slug={slug}>
      <Workspace
        slug={slug}
        page={page}
        id={id}
        org={org}
        auth={auth}
        user={user}
        notice={notice}
        onDismiss={() => setNotice(null)}
        onLeave={() => setNotice((shown) => (shown?.keep ? shown : null))}
      />
    </ConeMember>
  );
}

/** The header and the menu until Firebase knows the user: no data of a page, and no bars. */
function BootShell({ slug, page, name, kept }: { slug: string; page: Page; name?: string; kept: KeptShell | null }) {
  const wide = useWide();
  const hash = useHash();
  const [collapsed] = useState(() => stored(COLLAPSED_KEY) === "1");
  const base = `/o/${encodeURIComponent(slug)}`;
  const groups = kept ? visible(kept.views) : [];
  const menuGroups = kept ? menuFor(kept.roles, groups) : [];
  const admin = adminPath(menuGroups);
  return (
    <>
      <header>
        <div className="header-start">
          <button type="button" className="icon-button menu-toggle" aria-label="Open the menu">
            <MenuIcon />
          </button>
          <strong className="org-name">{name}</strong>
        </div>
        <HeaderSearch onOpen={() => undefined} />
        <div className="header-end">
          {admin ? (
            <span className="settings-link admin-link">
              <WrenchIcon size={22} />
              <span className="admin-label">Admin</span>
            </span>
          ) : (
            !kept && <AdminSlot />
          )}
          <span className="boot-avatar" />
        </div>
      </header>
      <div className={wide && !collapsed ? "shell with-menu" : "shell"}>
        {wide && (
          <aside className="side" hidden={collapsed}>
            {kept && (
              <SideMenu
                groups={menuGroups}
                roles={kept.roles}
                base={base}
                current={currentId(page, hash, undefined, groups)}
                exact={isExact(page)}
                pins={kept.pins}
                pinProblem=""
                onPin={() => undefined}
                uid={kept.uid}
              />
            )}
          </aside>
        )}
        <div className="content">
          <main className="wide" aria-busy="true" />
        </div>
      </div>
    </>
  );
}

function OtherAccount(props: { name: string; current: string; onUse: () => void; onStay: () => void }) {
  useTitle(`Sign in · ${props.name}`);
  return (
    <main className="narrow" data-testid="link-other-user">
      <h1>Sign in to {props.name}</h1>
      <p>You are signed in as {props.current}. This sign-in link is for another account.</p>
      <div className="choices">
        <button className="primary" data-testid="use-link" onClick={props.onUse}>
          Sign out and use the link
        </button>
        <button data-testid="keep-user" onClick={props.onStay}>
          Stay signed in as {props.current}
        </button>
      </div>
    </main>
  );
}

const PROVIDERS: { name: Provider; label: string }[] = [
  { name: "google", label: "Google" },
  { name: "microsoft", label: "Microsoft" },
];

const labelOf = (name?: Provider) => PROVIDERS.find((p) => p.name === name)?.label ?? "provider";

// keep: the notice stays on a page change, because its steps go to other pages.
type Notice = { ok: boolean; text: string; keep?: boolean };

type Link = { kind: "none" } | { kind: "redeeming" } | { kind: "failed"; error: string } | { kind: "other"; token: string };

function SignIn(props: {
  slug: string;
  org: api.Bootstrap;
  auth: Auth;
  linkError: string;
  redirected: RedirectOutcome | null;
}) {
  const { slug, org, auth, linkError, redirected } = props;
  const [email, setEmail] = useState(savedEmail(slug) ?? "");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [detail, setDetail] = useState("");
  const [copied, setCopied] = useState<"" | "done" | "failed">("");
  const [busy, setBusy] = useState(false);
  const emailField = useRef<HTMLInputElement>(null);
  const primaryButton = useRef<HTMLButtonElement>(null);
  const [saved] = useState(() => savedEmail(slug));
  const hint = emailDomain(email.trim()) ? email.trim() : null;
  const providers = PROVIDERS.filter((p) => org.sign_in.includes(p.name));
  const emailForm = org.sign_in.includes("email_link");
  const wanted = primaryFor(saved);
  const primary =
    providers.some((p) => p.name === wanted) || (wanted === "email" && emailForm) ? wanted : providers[0]?.name;
  useTitle(`Sign in · ${org.name}`);

  useEffect(() => {
    if (linkError) (emailField.current ?? primaryButton.current)?.focus();
  }, [linkError]);
  useEffect(() => {
    if (providers.some((p) => p.name === "google")) prepareProvider("google", org.identity.google_client_id);
  }, [org]);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setNotice("");
    const address = email.trim();
    if (!emailDomain(address)) {
      setError("Enter your email address, for example name@your-company.com.");
      return;
    }
    if (address.length > MAX_EMAIL) {
      setError("Enter a shorter email address.");
      return;
    }
    setBusy(true);
    try {
      rememberEmail(address, slug);
      await api.signinLink(address, slug);
      setNotice(sentText(address));
    } catch (e) {
      setError(sendError(e));
    } finally {
      setBusy(false);
    }
  };
  const failed = async (name: Provider, label: string, e: unknown) => {
    const linkable = errorCode(e) === "auth/account-exists-with-different-credential";
    const address = linkable && org.sign_in.includes("email_link") ? keepPending(slug, name, e as AuthError) : null;
    if (!address) {
      setError(providerError(e, label));
      setDetail(providerDetail(e));
      return;
    }
    try {
      await api.signinLink(address, slug, name);
      setNotice(connectText(address, label));
    } catch (x) {
      dropPending();
      setError(sendError(x));
    }
  };
  const withProvider = async (name: Provider, label: string) => {
    setError("");
    setDetail("");
    setCopied("");
    setNotice("");
    try {
      await signInWithProvider(auth, slug, name, hint, org.identity.google_client_id);
    } catch (e) {
      await failed(name, label, e);
    }
  };
  const shown = useRef<RedirectOutcome | null>(null);
  useEffect(() => {
    if (!redirected || shown.current === redirected) return;
    shown.current = redirected;
    if (redirected.lost) setError(redirectLostText(labelOf(redirected.name)));
    else if (redirected.error !== undefined) void failed(redirected.name, labelOf(redirected.name), redirected.error);
  }, [redirected]);

  const copyDetail = () =>
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(detail))
      .then(
        () => setCopied("done"),
        () => setCopied("failed"),
      );

  return (
    <main className="narrow">
      <h1>Sign in to {org.name}</h1>
      {linkError && !notice && (
        <p role="alert" className="error" data-testid="link-error">
          {linkErrorText(linkError, emailForm, providers.map((p) => p.label))}
        </p>
      )}
      {providers.length > 0 && (
        <div className="choices">
          {providers.map(({ name, label }) => (
            <button
              key={name}
              ref={name === primary ? primaryButton : undefined}
              className={name === primary ? "primary" : undefined}
              data-testid={`sign-in-${name}`}
              onClick={() => withProvider(name, label)}
            >
              Sign in with {label}
            </button>
          ))}
          {hint && (
            <p className="hint">
              {providers.map((p) => p.label).join(" and ")} will offer the account {hint} first.
            </p>
          )}
        </div>
      )}
      {providers.length > 0 && emailForm && (
        <p className="or" aria-hidden="true">
          or
        </p>
      )}
      {emailForm && (
        <form onSubmit={send} noValidate>
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            ref={emailField}
            data-testid="sign-in-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button className={primary === "email" ? "primary" : undefined} data-testid="send-link" disabled={busy}>
            {busy ? "Sending…" : "Email me a sign-in link"}
          </button>
        </form>
      )}
      {notice && (
        <p role="status" data-testid="link-sent">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {detail && (
        <p className="copy">
          <button type="button" data-testid="copy-details" onClick={copyDetail}>
            Copy details
          </button>
          <span role="status" className={copied === "failed" ? "copy-failed" : undefined} data-testid="copy-status">
            {copied === "done" && "Copied."}
            {copied === "failed" && (
              <>
                Could not copy. Select this text: <code>{detail}</code>
              </>
            )}
          </span>
        </p>
      )}
      <p>
        <a href="/" className="switch-org" onClick={forgetOrg} data-testid="switch-org">
          Use another email address
        </a>
      </p>
    </main>
  );
}

function Workspace({
  slug,
  page,
  id,
  org,
  auth,
  user,
  notice,
  onDismiss,
  onLeave,
}: {
  slug: string;
  page: Page;
  id?: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  notice: Notice | null;
  onDismiss: () => void;
  onLeave: () => void;
}) {
  const base = `/o/${encodeURIComponent(slug)}`;
  const shown = useRef(`${page}/${id}`);
  const [views, setViews] = useState<string[] | null | undefined>(undefined);
  // When /api/me/can fails, each page asks the API itself and shows the API's answer.
  const may = (view: string) => (views === null ? true : views?.includes(view));
  // The kept shell is a paint: `may`, the page redirects and the edit rights wait for /api/me/can.
  const [kept] = useState(() => readShell(slug, user.uid));
  // A page with kept data opens with the kept rights; the API still refuses what the role cannot do.
  const mayPaint = (view: string) => (keptMay(kept, views, view) ? true : may(view));
  const canChecked = useRef(false);
  const paintViews = views === undefined ? kept?.views : (views ?? undefined);
  const [edits, setEdits] = useState<string[] | undefined>(undefined);
  const [roles, setRoles] = useState<string[] | null | undefined>(undefined);
  const paintRoles = roles === undefined ? kept?.roles : roles;
  const menuKnown = (roles !== undefined && views !== undefined) || kept !== null;
  const wide = useWide();
  const hash = useHash();
  const [collapsed, setCollapsed] = useState(() => stored(COLLAPSED_KEY) === "1");
  const [drawer, setDrawer] = useState(false);
  const [jump, setJump] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const recentKey = `${RECENT_KEY}.${slug}.${user.uid}`;
  const [visits, setVisits] = useState<Visit[]>(() => readVisits(stored(recentKey)));
  const groups = visible(paintViews);
  const menuGroups = menuFor(paintRoles, groups);
  const current = currentId(page, hash, undefined, groups);
  const crumb = useCrumbLabel();
  const [pins, setPins] = useState<string[]>(kept?.pins ?? []);
  const [pinProblem, setPinProblem] = useState("");
  const openAll = useMenuAll(user);
  useEffect(() => {
    const load = async () => {
      try {
        setPins(await api.pins(await user.getIdToken()));
      } catch {
        // Without the pins, the menu shows only its groups.
      }
    };
    const shown = () => {
      if (document.visibilityState === "visible") void load();
    };
    void load();
    document.addEventListener("visibilitychange", shown);
    return () => document.removeEventListener("visibilitychange", shown);
  }, [user]);
  const pin = useCallback((id: string) => {
    const before = pins;
    const on = !before.includes(id);
    setPins(togglePin(before, id));
    setPinProblem("");
    void (async () => {
      try {
        const token = await user.getIdToken();
        // Another tab can change the pins, so apply this one change to the saved list.
        const saved = await api.pins(token);
        setPins(await api.setPins(token, setPin(saved, id, on)));
      } catch {
        setPins(before);
        setPinProblem("The pin was not saved. Try again.");
      }
    })();
  }, [pins, user]);
  const tabs = useMemo(() => ({ groups, base, current, pins, onPin: pin, known: menuKnown }), [groups, base, current, pins, pin, menuKnown]);
  const scope = useMemo(() => userScope(slug, user.uid), [slug, user.uid]);
  // Before the effects of the page: a view that starts first finds no kept data.
  useLayoutEffect(() => void openScope(slug, user.uid), [slug, user.uid]);
  useEffect(() => startSync({ timeZone: org.time_zone, token: () => user.getIdToken() }), [slug, user, org.time_zone]);
  const online = useOnline();
  const idToken = useCallback(() => user.getIdToken(), [user]);
  useOutboxSender(scope, idToken, true);
  const outbox = useOutbox(scope);
  const outboxWaiting = (outbox.all ?? []).filter(waiting).length;
  const [lastOnline, setLastOnline] = useState<number | null>(null);
  const windowHours = offlineWindowHours();
  useEffect(() => {
    void scope.read<number>(dayKeys.online).then((kept) => setLastOnline(kept?.value ?? null));
  }, [scope, online]);
  const keepOffline = async (driver: boolean) => {
    const now = Date.now();
    void scope.write(dayKeys.online, now);
    setLastOnline(now);
    if (!driver || shown.current.startsWith("today/")) return;
    try {
      void refreshDay(scope, await user.getIdToken(), null).catch(() => undefined);
    } catch {
      // Without a token the kept day stays in use.
    }
  };
  useEffect(() => {
    let current = true;
    let warm: ReturnType<typeof warmViews> | null = null;
    const apply = ({ value: found, at }: Found<Awaited<ReturnType<typeof api.can>>>) => {
      if (!current) return;
      if (at === null) canChecked.current = true;
      setMemberNames(found.member_names);
      setViews(renamedModules(found.view));
      setEdits(renamedModules(found.edit));
      setRoles(found.roles ?? []);
      const checked = Promise.all([checkRoles(found.roles ?? []), checkAccess(found.access_key)]);
      if (at === null) void keepOffline(found.view.includes("route_run"));
      if (at === null && !warm)
        void checked.then(() => {
          if (current && !warm) warm = warmViews(renamedModules(found.view), found.roles ?? [], () => user.getIdToken());
        });
    };
    const failed = () => {
      if (!current) return;
      dropShell(slug);
      setViews(null);
      setEdits(undefined);
      setRoles(null);
    };
    cachedFetch(scope.key(dayKeys.can), async () => api.can(await user.getIdToken()))
      .then((found) => {
        apply(found);
        found.fresh?.then(apply, (e) => !isNetworkError(e) && failed());
      })
      .catch(failed);
    return () => {
      current = false;
      warm?.cancel();
    };
  }, [user, scope]);
  useEffect(() => {
    if (canChecked.current && views && roles) keepShell(slug, { uid: user.uid, org, views, roles, pins });
  }, [slug, user.uid, org, views, roles, pins]);
  useEffect(() => {
    const outside = !id && Array.isArray(views) && !pageAllowed(page, views);
    if ((page !== "home" && !outside) || roles === undefined) return;
    const home = roles === null ? undefined : homePage(roles, views ?? undefined, undefined, !wide);
    const path = home ? pathOf(home) : roles === null ? "/tanks" : "/settings";
    history.replaceState(null, "", `${base}${path}`);
    dispatchEvent(new PopStateEvent("popstate"));
  }, [page, id, roles, views, base, wide]);
  useEffect(() => {
    const moved = movedSection(page, hash);
    if (!moved) return;
    history.replaceState(null, "", `${base}${moved}`);
    dispatchEvent(new PopStateEvent("popstate"));
  }, [page, hash, base]);
  const listAllowed = (list: Page) => !Array.isArray(views) || pageAllowed(list, views);
  useEffect(() => rememberOrg(slug), [slug]);
  useEffect(() => {
    if (shown.current === `${page}/${id}`) return;
    shown.current = `${page}/${id}`;
    onLeave();
    if (!location.hash) document.querySelector<HTMLElement>("main h1")?.focus();
  }, [page, id]);
  useEffect(() => {
    const menuPage = pages().find((p) => p.id === current);
    const list = page === "tank" ? "tanks" : page === "lead" ? "leads" : page === "deal" || page === "agreement" ? "deals" : page === "rebate" ? "rebates" : page === "user" ? "users" : page === "role" ? "roles" : page === "unit" ? "inventory" : "accounts";
    const path = id ? `${base}/${list}/${encodeURIComponent(id)}` : menuPage ? `${base}${pathOf(menuPage)}` : "";
    if (!path || (!id && (views === undefined || (Array.isArray(views) && !pageAllowed(page, views))))) return;
    setVisits((before) => {
      const next = remember(before, path);
      store(recentKey, JSON.stringify(next));
      return next;
    });
  }, [base, page, id, current, recentKey, views]);
  useEffect(() => {
    if (wide) setDrawer(false);
  }, [wide]);
  useEffect(() => {
    const open = (event: globalThis.KeyboardEvent) => {
      const slash = event.key === "/" && !event.ctrlKey && !event.metaKey && !event.altKey && !typing(event.target);
      const k = event.key.toLowerCase() === "k" && (event.ctrlKey || event.metaKey) && !event.altKey;
      if (!slash && !k) return;
      if (document.querySelector("dialog[open]")) return;
      event.preventDefault();
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setJump(true);
    };
    addEventListener("keydown", open);
    return () => removeEventListener("keydown", open);
  }, []);
  const dismiss = () => {
    onDismiss();
    document.querySelector<HTMLElement>("main h1")?.focus();
  };
  const toggleMenu = () => {
    if (wide) {
      setCollapsed((before) => {
        store(COLLAPSED_KEY, before ? "0" : "1");
        return !before;
      });
    } else {
      setDrawer(true);
    }
  };
  const openJump = (from: HTMLElement | null) => {
    opener.current = from;
    setDrawer(false);
    setJump(true);
  };
  const closeDrawer = () => {
    setDrawer(false);
    refocus(toggleRef);
  };
  const menuShown = wide ? !collapsed : drawer;
  const admin = adminPath(menuGroups);
  const inAdmin = ancestors(current, menuGroups)[0]?.id === "admin";
  const menu = (
    <SideMenu
      groups={menuGroups}
      roles={paintRoles ?? []}
      base={base}
      current={current}
      exact={isExact(page)}
      pins={pins}
      pinProblem={pinProblem}
      onPin={pin}
      uid={user.uid}
      openAll={openAll}
      known={menuKnown}
      onFollow={
        wide
          ? undefined
          : () => {
              setDrawer(false);
              focusPage();
            }
      }
    />
  );
  return (
    <>
      <a
        className="skip-link"
        href="#content"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById("content")?.focus();
        }}
      >
        Skip to content
      </a>
      <header>
        <div className="header-start">
          <button
            ref={toggleRef}
            type="button"
            className="icon-button menu-toggle"
            data-testid="menu-toggle"
            aria-label={wide ? (collapsed ? "Show the menu" : "Hide the menu") : "Open the menu"}
            aria-expanded={menuShown}
            aria-controls={wide ? "side-menu" : "menu-dialog"}
            onClick={toggleMenu}
          >
            <MenuIcon />
          </button>
          <OrgSwitcher user={user} name={org.name} />
        </div>
        <HeaderSearch onOpen={openJump} />
        <div className="header-end">
          {admin ? (
            <a
              className="settings-link admin-link"
              aria-current={inAdmin ? (page === "admin" ? "page" : "true") : undefined}
              aria-label="Admin"
              title="Admin"
              href={`${base}${admin}`}
              onClick={go}
              data-testid="nav-admin"
            >
              <WrenchIcon size={22} />
              <span className="admin-label">Admin</span>
            </a>
          ) : (
            !menuKnown && <AdminSlot />
          )}
          <InstallButton />
          <SyncBadge slug={slug} base={base} uid={user.uid} />
          <AccountMenu user={user} base={base} onSignOut={() => void signOutHome(auth)} />
        </div>
      </header>
      <div className={wide && !collapsed ? "shell with-menu" : "shell"}>
        {wide && (
          <aside id="side-menu" className="side" hidden={collapsed} data-testid="side-menu">
            {menu}
          </aside>
        )}
        <TabsContext.Provider value={tabs}>
          <div id="content" className="content" tabIndex={-1}>
            {notice && (
              <div className="notice">
                <div
                  className={notice.ok ? "notice-box" : "notice-box bad"}
                  role={notice.ok ? "status" : "alert"}
                  data-testid="workspace-notice"
                >
                  <p>
                    <strong>{notice.ok ? "Done:" : "Problem:"}</strong> {notice.text}
                  </p>
                  <button type="button" className="quiet" data-testid="dismiss-notice" onClick={dismiss}>
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            {!online && signInDue(lastOnline, windowHours) && (
              <div className="notice">
                <div className="notice-box bad" role="alert" data-testid="offline-sign-in">
                  <p>
                    <strong>Sign in needed:</strong> This phone has been offline for more than {windowHours} h. When you have a signal, sign in
                    again. The data on this phone stays.
                  </p>
                </div>
              </div>
            )}
            {outbox.signIn && outboxWaiting > 0 && (
              <div className="notice">
                <div className="notice-box bad" role="alert" data-testid="outbox-sign-in">
                  <p>
                    <strong>Sign in needed:</strong> {outboxWaiting} entries wait on this phone. Sign in again to send them. They stay on the phone.
                  </p>
                  <button type="button" className="quiet" onClick={() => void signOut(auth)}>
                    Sign in again
                  </button>
                </div>
              </div>
            )}
            {page !== "home" && page !== "missing" && (
              <div className="crumb-row no-print">
                <Breadcrumbs groups={groups} base={base} current={current} exact={isExact(page)} leaf={crumb} />
                <ShareButton what="this page" testId="share-page" />
              </div>
            )}
            {page === "home" ? (
              <main data-testid="home-loading" />
            ) : page === "settings" ? (
              <YourSettings org={org} auth={auth} user={user} hash={hash} />
            ) : page === "admin" ? (
              <AdminArea
                base={base}
                org={org}
                user={user}
                forecast={Boolean(paintViews?.includes("forecast"))}
                lookup={Boolean(paintViews?.includes("lookup"))}
                calendar={Boolean(paintViews?.includes("calendar"))}
                holiday={Boolean(paintViews?.includes("holiday"))}
                areas={Boolean(paintViews?.includes("area"))}
                alerts={Boolean(paintViews?.includes("alert"))}
                visitLabels={Boolean(paintViews?.includes("visit_label"))}
                hash={hash}
              />
            ) : page === "fleet" ? (
              <SectionPage org={org} page={page} canView={may("fleet")}>
                <FleetSettings user={user} timeZone={org.time_zone} />
              </SectionPage>
            ) : page === "route-planner" ? (
              <SectionPage org={org} page={page} canView={may("fleet")}>
                <PlannerSettings user={user} />
              </SectionPage>
            ) : page === "containers" ? (
              <ContainerSizes base={base} org={org} auth={auth} user={user} canView={may("account")} settings={may("forecast") === true} />
            ) : page === "size-savings" ? (
              <SizeSavings base={base} org={org} auth={auth} user={user} canView={may("account")} />
            ) : page === "rebate-checks" ? (
              <SectionPage org={org} page={page} canView={may("payout")}>
                <PayoutSettings auth={auth} user={user} />
              </SectionPage>
            ) : page === "accounts" ? (
              <AccountList base={base} slug={slug} org={org} auth={auth} user={user} canView={may("account")} />
            ) : page === "issues" ? (
              <IssueList
                base={base}
                org={org}
                auth={auth}
                user={user}
                canView={may("issue")}
                canTake={edits && edits.includes("issue")}
              />
            ) : page === "work-orders" ? (
              <WorkOrderList base={base} org={org} auth={auth} user={user} canView={may("container")} />
            ) : page === "inventory" ? (
              <UnitList base={base} org={org} auth={auth} user={user} canView={may("inventory")} />
            ) : page === "pickups" ? (
              <PickupsPage base={base} org={org} auth={auth} user={user} canView={mayPaint("review")} />
            ) : page === "overview" ? (
              <OverviewPage base={base} org={org} auth={auth} user={user} canView={mayPaint("review")} />
            ) : page === "review" ? (
              <ReviewPage base={base} org={org} auth={auth} user={user} canView={may("review")} />
            ) : page === "loads" ? (
              <LoadList org={org} auth={auth} user={user} canView={may("load")} />
            ) : page === "cycle" ? (
              <CyclePage base={base} org={org} auth={auth} user={user} canView={may("route")} />
            ) : page === "routes" ? (
              <RoutesPage base={base} org={org} auth={auth} user={user} canView={may("route_run")} />
            ) : page === "fill" ? (
              <FillViewPage base={base} org={org} auth={auth} user={user} canView={may("fill_view")} />
            ) : page === "today" ? (
              <TodayPage slug={slug} base={base} org={org} auth={auth} user={user} canView={may("route_run")} canVisit={may("visit") === true} />
            ) : page === "visit" ? (
              <VisitPage slug={slug} base={base} org={org} auth={auth} user={user} canView={may("visit")} />
            ) : page === "requests" ? (
              <RequestsPage base={base} org={org} auth={auth} user={user} canView={may("pickup_request")} />
            ) : page === "messages" ? (
              <MessagesPage base={base} org={org} auth={auth} user={user} canView={may("customer_message")} />
            ) : page === "sync" ? (
              <SyncPage slug={slug} base={base} org={org} user={user} canView={may("visit")} />
            ) : page === "visits" ? (
              <VisitsPage base={base} org={org} auth={auth} user={user} canView={may("visit")} canEdit={Boolean(edits?.includes("visit"))} />
            ) : page === "parity" ? (
              <ParityPage base={base} org={org} auth={auth} user={user} canView={may("parity")} />
            ) : page === "scorecard" ? (
              <ScorecardPage org={org} auth={auth} user={user} canView={mayPaint("scorecard")} />
            ) : page === "leads" ? (
              <LeadList base={base} org={org} auth={auth} user={user} canView={may("prospect")} />
            ) : page === "follow-ups" ? (
              <FollowUpList base={base} org={org} auth={auth} user={user} canView={may("prospect")} />
            ) : page === "deals" ? (
              <DealList base={base} org={org} auth={auth} user={user} canView={may("activation")} />
            ) : page === "renewals" ? (
              <RenewalList base={base} org={org} auth={auth} user={user} canView={may("contract")} />
            ) : page === "rebates" ? (
              <RebateList base={base} org={org} auth={auth} user={user} canView={may("payout")} />
            ) : page === "users" ? (
              <UserList base={base} org={org} auth={auth} user={user} canView={may("member")} />
            ) : page === "roles" ? (
              <RoleList base={base} org={org} auth={auth} user={user} canView={may("member")} />
            ) : page === "keys" ? (
              <KeyList org={org} auth={auth} user={user} canView={may("api_key")} />
            ) : page === "activity" ? (
              <ActivityPage base={base} org={org} auth={auth} user={user} canView={may("activity")} />
            ) : page === "cutover" ? (
              <CutoverPage base={base} org={org} auth={auth} user={user} canView={may("cutover")} />
            ) : page === "exports" ? (
              <ExportsPage org={org} auth={auth} user={user} canView={may("export")} />
            ) : page === "import" ? (
              <ImportPage base={base} org={org} auth={auth} user={user} canView={may("import")} canEdit={Boolean(edits?.includes("import"))} canParity={may("parity") === true} />
            ) : page === "missing" ? (
              <main data-testid="page-missing">
                <h1 tabIndex={-1}>Page not found</h1>
                <p>
                  This address is not a page of {org.name}.{" "}
                  <a href={`${base}/tanks`} onClick={go}>
                    Go to the containers
                  </a>
                </p>
              </main>
            ) : page === "account" && id ? (
              <AccountPage key={id} id={id} base={base} org={org} auth={auth} user={user} listAllowed={listAllowed("accounts")} canVisits={Boolean(views?.includes("visit"))} canSensors={may("tank") === true} />
            ) : page === "lead" && id ? (
              <LeadPage key={id} id={id} base={base} org={org} auth={auth} user={user} listAllowed={listAllowed("leads")} />
            ) : page === "deal" && id ? (
              <DealPage key={id} id={id} base={base} org={org} auth={auth} user={user} listAllowed={listAllowed("deals")} />
            ) : page === "user" && id ? (
              <UserPage key={id} id={id} base={base} org={org} auth={auth} user={user} />
            ) : page === "role" && id ? (
              <RolePage key={id} id={id} base={base} org={org} auth={auth} user={user} />
            ) : page === "unit" && id ? (
              <UnitPage key={id} id={id} base={base} org={org} auth={auth} user={user} />
            ) : page === "rebate" && id ? (
              <RebateRun key={id} id={id} base={base} org={org} auth={auth} user={user} listAllowed={listAllowed("rebates")} />
            ) : page === "agreement" && id ? (
              <AgreementPage key={id} id={id} base={base} org={org} auth={auth} user={user} />
            ) : page === "tank" && id ? (
              <TankPage key={id} id={id} base={base} org={org} auth={auth} user={user} />
            ) : (
              <TankList base={base} org={org} auth={auth} user={user} canSummary={(views === undefined ? kept?.views.includes("review") : may("review")) ?? null} />
            )}
          </div>
        </TabsContext.Provider>
      </div>
      {!wide && (
        <Drawer open={drawer} onDismiss={closeDrawer}>
          {menu}
        </Drawer>
      )}
      <Jump
        open={jump}
        user={user}
        base={base}
        groups={menuGroups}
        views={views ?? undefined}
        visits={visits}
        pins={pins}
        onFollow={() => {
          setJump(false);
          focusPage();
        }}
        onDismiss={() => {
          setJump(false);
          refocus(opener.current);
        }}
      />
    </>
  );
}

/** After a dialog closes, give the focus to the heading of the page that it opened. */
function focusPage() {
  requestAnimationFrame(() => {
    if (!location.hash) document.querySelector<HTMLElement>("main h1")?.focus();
  });
}

const WRAP_HEADS = new Set(["left", "visit", "last"]); // the Levels table fits at 1280 with the menu open

const COLLAPSED_KEY = "xflow.menuCollapsed";
const RECENT_KEY = "xflow.recent";

/** The settings of the signed-in person, under "Your settings" in the avatar menu. */
function YourSettings({ org, auth, user, hash }: { org: api.Bootstrap; auth: Auth; user: User; hash: string }) {
  const [theme, setTheme] = useState<Theme>(currentTheme);
  const version = useVersionText();
  useEffect(() => watchTheme(setTheme), []);
  useSection(hash, "");
  useTitle(`Your settings · ${org.name}`);
  const choose = (next: Theme) => {
    applyTheme(next);
    setTheme(next);
  };
  return (
    <main className="settings form-page" data-testid="your-settings">
      <h1 tabIndex={-1}>Your settings</h1>
      <p className="sub">These settings change only your own view and mail.</p>
      <FormSection id="your-account" title="Your account" description="Your sign-in, the version of the app and your organizations." hash={hash}>
        <fieldset>
          <legend>Your account</legend>
          <p className="account-email">Signed in as {user.email}</p>
          <p className="sub" data-testid="settings-version" title={versionTitle()}>
            {version}
          </p>
          <OrgSettings user={user} />
          <button data-testid="settings-sign-out" onClick={() => signOutHome(auth)}>
            Sign out
          </button>
        </fieldset>
      </FormSection>
      <FormSection id="theme" title="Theme" description="The dark or the light colours of the app." hash={hash}>
        <fieldset>
          <legend>Theme</legend>
          {(["dark", "light"] as const).map((option) => (
            <label key={option}>
              <input
                type="radio"
                name="theme"
                data-testid={`theme-${option}`}
                checked={theme === option}
                onChange={() => choose(option)}
              />
              {option === "dark" ? "Dark" : "Light"}
            </label>
          ))}
        </fieldset>
      </FormSection>
      <FormSection id="notifications" title="Notifications" description="The mail and the alerts that you get." hash={hash}>
        <NotificationSettings auth={auth} user={user} />
      </FormSection>
      <FormSection id="menu" title="Menu" description="How the side menu opens on a new phone or computer." hash={hash}>
        <MenuSettings auth={auth} user={user} />
      </FormSection>
    </main>
  );
}

const PAGE_TEXT: Partial<Record<Page, string>> = {
  fleet: "The depots and the trucks that the route planner uses. A depot is a hub.",
  "route-planner": "How the route planner makes the routes of each workday.",
  "rebate-checks": "The minimum amount of a rebate check. The check runs are on Pay > Rebates.",
};

/** A settings section with its own menu place, so its h1 is its menu label. */
function SectionPage({ org, page, canView, children }: { org: api.Bootstrap; page: Page; canView: boolean | undefined; children: ReactNode }) {
  const label = pages().find((node) => node.page === page)?.label ?? "Admin";
  useTitle(`${label} · ${org.name}`);
  return (
    <main className="settings form-page single" data-testid={`page-${page}`}>
      <h1 tabIndex={-1}>{label}</h1>
      {PAGE_TEXT[page] && <p className="form-section-desc">{PAGE_TEXT[page]}</p>}
      {canView === false ? <p>Your role does not show this page.</p> : canView === undefined ? <Skeleton shape="form" /> : children}
    </main>
  );
}

/** The settings of the organization, under the wrench. Each section shows only with its view. */
function AdminArea({
  base,
  org,
  user,
  forecast,
  lookup,
  calendar,
  holiday,
  areas,
  alerts,
  visitLabels,
  hash,
}: {
  base: string;
  org: api.Bootstrap;
  user: User;
  forecast: boolean;
  lookup: boolean;
  calendar: boolean;
  holiday: boolean;
  areas: boolean;
  alerts: boolean;
  visitLabels: boolean;
  hash: string;
}) {
  useSection(hash, [forecast, lookup, calendar, holiday, areas, alerts, visitLabels].join());
  useTitle(`Admin · ${org.name}`);
  return (
    <main className="settings form-page" data-testid="admin-area">
      <h1 tabIndex={-1}>Admin</h1>
      <p className="sub">These settings change the organization for everyone.</p>
      {forecast && (
        <FormSection id="forecast" title="Forecast limits" description="The limits of the flags on the Accounts page." hash={hash}>
          <ForecastSettings user={user} />
        </FormSection>
      )}
      {forecast && (
        <FormSection id="sensor-rules" title="Sensor rules" description="The limits of the sensor rules that open issues each night." hash={hash}>
          <SensorRuleSettings user={user} />
        </FormSection>
      )}
      {forecast && (
        <FormSection id="containers" title="Containers" description="The standard container sizes and the size margin." hash={hash}>
          <ContainerSettings user={user} />
        </FormSection>
      )}
      {calendar && (
        <FormSection id="calendar" title="Cycle calendar" description="The week start, the cycle start, the holiday rule and the early-pull margin." hash={hash}>
          <CalendarSettings user={user} />
        </FormSection>
      )}
      {holiday && (
        <FormSection id="holidays" title="Holidays" description="The holidays of the organization. A stop on a holiday moves by the calendar rule." hash={hash}>
          <HolidaySettings user={user} />
        </FormSection>
      )}
      {areas && (
        <FormSection id="sales-areas" title="Sales areas" description="The salespeople of each area. A new lead in the area goes to one of them." hash={hash}>
          <SalesAreaSettings base={base} user={user} />
        </FormSection>
      )}
      {lookup && (
        <FormSection id="sales-terms" title="Sales terms" description="The account number range of each state and the deal limits." hash={hash}>
          <SalesSettings user={user} />
        </FormSection>
      )}
      {lookup && (
        <FormSection id="pre-trip" title="Pre-trip check" description="The items that a driver checks before the route starts." hash={hash}>
          <PreTripSettings user={user} />
        </FormSection>
      )}
      {visitLabels && (
        <FormSection id="visit-labels" title="Visit labels" description="The labels that a driver puts on a visit with one tap." hash={hash}>
          <VisitLabelSettings user={user} />
        </FormSection>
      )}
      {lookup && (
        <FormSection id="issue-reminders" title="Issue reminders" description="The days that an issue stays open before its assignee gets a reminder mail." hash={hash}>
          <IssueReminderSettings user={user} />
        </FormSection>
      )}
      {lookup && (
        <FormSection id="place-check" title="Away check" description="The away check on or off, and its distance from the stop pin." hash={hash}>
          <PlaceCheckSettings user={user} />
        </FormSection>
      )}
      {alerts && (
        <FormSection id="telegram-alerts" title="Telegram alerts" description="The sensor rule issues that go to a Telegram channel." hash={hash}>
          <TelegramAlertSettings user={user} />
        </FormSection>
      )}
      {lookup && (
        <FormSection id="message-mail" title="Message mail" description="The people who get a mail when a customer sends a message in the portal." hash={hash}>
          <MessageMailSettings user={user} />
        </FormSection>
      )}
      {lookup && <PortalContactSettings user={user} />}
    </main>
  );
}

export function TankList({ base, org, auth, user, canSummary = false }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canSummary?: boolean | null }) {
  const view = useView(CONTAINERS.name, async (signal) => api.containerList(await user.getIdToken(), signal));
  const list = view.data;
  const failed = view.error && !view.checking ? refreshFailure(view.error) : null;
  const [filter, setFilter] = useState<TankFilter>(() => readTankFilter(location.search));
  const search = useRef<HTMLInputElement>(null);
  const now = useClock();
  useTitle(`Containers · Levels · ${org.name}`);
  const change = (next: Partial<TankFilter>) => {
    const value = { ...filter, ...next };
    setFilter(value);
    const params = writeTankFilter(value);
    for (const key of ["sort", "tsort"]) {
      const kept = new URLSearchParams(location.search).get(key);
      if (kept) params.set(key, kept);
    }
    const text = params.toString();
    history.replaceState(history.state, "", `${location.pathname}${text ? `?${text}` : ""}${location.hash}`);
  };

  const load = view.reload;

  const [sort, setSort] = useSort("sort", CONTAINER_COLUMNS);
  const [tankSort, setTankSort] = useSort("tsort", TANK_COLUMNS);
  const rows = useMemo(() => (list ? rowsOf(list) : []), [list]);
  const shown = useMemo(() => filterRows(rows, filter), [rows, filter]);
  const sorted = useMemo(() => (sort ? sortRows(shown, CONTAINER_COLUMNS, sort) : fullestFirst(shown)), [shown, sort]);
  const tanks = list ? filterTanks(list.tanks, filter) : [];
  const sortedTanks = sortRows([...tanks].sort(byAttention(now)), TANK_COLUMNS, tankSort);
  const hidden = list ? unlinkedCount(list.tanks) : 0;
  const any = Boolean(list && (rows.length > 0 || list.tanks.length > 0));
  const firstLoad = view.at === null && !failed;
  return (
    <main className="wide">
      <TabHead />
      <p className="freshness" data-testid="freshness">
        {view.at === null ? (
          failed ? (
            ""
          ) : (
            <SkeletonBar width="9rem" />
          )
        ) : failed ? (
          <span data-testid="view-stale">{failedText(view.at, org.time_zone)}</span>
        ) : view.checked && !view.checking ? (
          <span data-testid="view-updated">Updated {clockTime(new Date(view.at), org.time_zone)}</span>
        ) : (
          <span data-testid="view-kept">{keptText(view.at)}</span>
        )}
      </p>
      {failed && <RefreshFailed failed={failed} at={view.at} timeZone={org.time_zone} auth={auth} retry={load} />}
      {canSummary !== false && <PickupsCharts base={base} auth={auth} user={user} day={localNow(now, org.time_zone).day} today={localNow(now, org.time_zone).day} timeZone={org.time_zone} only hold={canSummary === null} />}
      {(firstLoad || any) && (
        <form className="filters" role="search" aria-label="Filter the containers" onSubmit={(e) => e.preventDefault()}>
          <div>
            <label htmlFor="tank-search">Search</label>
            <input
              id="tank-search"
              ref={search}
              type="search"
              data-testid="tank-search"
              placeholder="Account, name, area or sensor"
              value={filter.q}
              onChange={(e) => change({ q: e.target.value })}
            />
          </div>
          {(firstLoad || rows.length > 0) && (
            <div>
              <label htmlFor="tank-status-filter">Status</label>
              <select
                id="tank-status-filter"
                data-testid="tank-status-filter"
                value={filter.status}
                onChange={(e) => change({ status: e.target.value as TankFilter["status"] })}
              >
                <option value="">All</option>
                {(Object.keys(STATUS_TEXT) as api.Account["status"][]).map((value) => (
                  <option key={value} value={value}>
                    {STATUS_TEXT[value]}
                  </option>
                ))}
              </select>
            </div>
          )}
          {(firstLoad || rows.length > 0) && (
            <div>
              <label htmlFor="tank-week-filter">Rotation week</label>
              <select
                id="tank-week-filter"
                data-testid="tank-week-filter"
                value={filter.week}
                onChange={(e) => change({ week: Number(e.target.value) })}
              >
                <option value={0}>All</option>
                {[1, 2, 3, 4].map((week) => (
                  <option key={week} value={week}>
                    Week {week}
                  </option>
                ))}
              </select>
            </div>
          )}
          {(firstLoad || rows.length > 0) && (
            <div>
              <label htmlFor="tank-source-filter">Level from</label>
              <select
                id="tank-source-filter"
                data-testid="tank-source-filter"
                value={filter.source}
                onChange={(e) => change({ source: e.target.value as TankFilter["source"] })}
              >
                <option value="">All</option>
                <option value="sensor">Sensor</option>
                <option value="estimate">Estimate</option>
                <option value="none">No level</option>
              </select>
            </div>
          )}
          {hidden > 0 && (
            <label className="check">
              <input
                type="checkbox"
                data-testid="tank-unlinked-filter"
                checked={filter.unlinked}
                onChange={(e) => change({ unlinked: e.target.checked })}
              />
              {unlinkedText(hidden)}
            </label>
          )}
          {isTankFiltered(filter) && (
            <button
              type="button"
              className="quiet"
              data-testid="clear-filters"
              onClick={() => {
                change({ ...NO_TANK_FILTER, unlinked: filter.unlinked });
                search.current?.focus();
              }}
            >
              Clear the filters
            </button>
          )}
        </form>
      )}
      {list && rows.length > 0 && (
        <p className="freshness">
          <strong role="status" data-testid="tank-summary">
            {containerSummary(rows, shown.length)}
          </strong>
        </p>
      )}
      {firstLoad && (
        <>
          <p className="freshness">
            <SkeletonBar width="14rem" />
          </p>
          <SkeletonTools />
          <Skeleton shape="table" columns={6} rows={8} />
        </>
      )}
      {list && !any && (
        <p data-testid="tanks-empty">
          No containers yet. If you expect some, ask an administrator of {org.name} for a role.
        </p>
      )}
      {list && any && sorted.length === 0 && sortedTanks.length === 0 && (
        <p data-testid="tanks-no-match">{rows.length === 0 && hidden > 0 && !filter.unlinked ? "No containers of an account to show." : "No containers match the filters."}</p>
      )}
      {sorted.length > 0 && (
        <SortSelect id="container-sort" columns={CONTAINER_COLUMNS} sort={sort} onSort={setSort} fallback="Fullest first" />
      )}
      {sorted.length > 0 && (
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Containers">
          <table className="list" data-testid="container-list" data-cols="container-sort">
            <thead>
              <tr>
                {CONTAINER_COLUMNS.map((column) => (
                  <SortTh key={column.key} column={column} sort={sort} onSort={setSort} className={WRAP_HEADS.has(column.key) ? "wrap-head" : undefined} />
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <ContainerRowView key={row.account.id} base={base} row={row} now={now} timeZone={org.time_zone} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sorted.length > 0 && (
        <p className="footnote">An estimate comes from the pickups, the visits and the sensors of the account. It is not a measurement.</p>
      )}
      {sortedTanks.length > 0 && (
        <section aria-labelledby="sensor-tanks">
          <h2 id="sensor-tanks" className={rows.length > 0 ? undefined : "visually-hidden"}>
            Containers with a sensor
          </h2>
          <SortSelect id="tank-sort" columns={TANK_COLUMNS} sort={tankSort} onSort={setTankSort} fallback="Needs attention first" />
          <table className="list" data-testid="tank-list" data-cols="tank-sort">
            <thead>
              <tr>
                {TANK_COLUMNS.map((column) => (
                  <SortTh key={column.key} column={column} sort={tankSort} onSort={setTankSort} />
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedTanks.map((tank) => (
                <TankRow key={tank.id} base={base} tank={tank} now={now} timeZone={org.time_zone} />
              ))}
            </tbody>
          </table>
          <p className="footnote">Battery values are estimates from the sensor and can be wrong.</p>
        </section>
      )}
    </main>
  );
}

/** "Oct 27 · 85%" as the day, then the rest on a line under it on a wide screen. */
function TwoLines({ text }: { text: string }) {
  const [first, ...rest] = text.split(" · ");
  return (
    <>
      {first}
      {rest.length > 0 && <span className="sub below">{rest.join(" · ")}</span>}
    </>
  );
}

function ContainerRowView({ base, row, now, timeZone }: { base: string; row: ContainerRow; now: Date; timeZone: string }) {
  const { account, containers } = row;
  const level = levelOf(row, now);
  const tank = row.container.tank;
  const tanks = rowTanks(row);
  const stale = level.observedAt ? isStale(level.observedAt, now) : false;
  return (
    <tr data-testid="container-row" data-account={account.number} className={stale && tank?.in_field ? "row-stale" : undefined}>
      <th scope="row" className="account-number">
        <a href={`${base}/accounts/${account.id}`} onClick={go} data-testid="container-account-link">
          {account.number}
        </a>
      </th>
      <td data-label="">
        <span className="clip name" title={account.name}>
          {account.name}
        </span>
      </td>
      <td data-label="Area">{account.plan?.area ?? "–"}</td>
      <td className="num" data-label="Containers" data-testid="container-sizes">
        {sizeText(containers)}
        <span className="sub below">{kindText(containers)}</span>
      </td>
      <td data-testid="tank-level" data-label="Level" className="wrap">
        <span className="level-cell">
          <ConeBar share={level.share} accountId={account.id} label={`${account.number} ${account.name}`} words={level.source === "sensor" ? [level.text, level.detail].filter(Boolean).join(" · ") : level.source === "estimate" ? `${level.text}, estimated` : undefined} /> <span className={`level level-${level.old ? "old" : (level.state ?? "none")}`}>{level.text}</span>
        </span>
        {level.note && <span className="sub below level-note">{level.note}</span>}
        {level.detail && (
          <span className="sub below level-detail">
            {detailParts(level.detail).map((part, i) => (part.band ? <span key={i} className="level-range">{part.text}</span> : part.text))}
          </span>
        )}
        {level.source === "estimate" && !level.observedAt && <span className="sub below level-source-line">{SOURCE_TEXT.estimate}</span>}
        {level.observedAt && (
          <span className="sub below">
            {SOURCE_TEXT.sensor} ·{" "}
            {!level.dated && (
              <time dateTime={level.observedAt} title={readingTime(level.observedAt, timeZone)}>
                {age(level.observedAt, now)}
              </time>
            )}
            {stale && (
              <span className="chip-stale" data-testid="tank-stale">
                ⚠ Stale
              </span>
            )}
          </span>
        )}
      </td>
      <td data-testid="level-source" data-label="Source">
        {level.source ? SOURCE_TEXT[level.source] : "–"}
      </td>
      <td data-label="Sensor">
        {tanks.length
          ? tanks.map((found) => (
              <span key={found.id} className="sensor-link">
                <a href={`${base}/accounts/${account.id}#sensor`} onClick={go} data-testid="tank-link" className="clip sensor" title={found.label}>
                  {found.label}
                </a>
                {!found.in_field && <TestChip />}
              </span>
            ))
          : "–"}
      </td>
      <td className="weeks-left" data-label="Weeks left">
        {weeksLeftText(account)}
      </td>
      <td data-label="Next visit">
        <Hint text="The next visit and the estimated full share at that visit." stop={false}>
          <TwoLines text={nextVisitText(account)} />
        </Hint>
      </td>
      <td data-label="Last pickup or visit">
        <TwoLines text={lastServiceText(account)} />
      </td>
      <td data-label="Status">{STATUS_TEXT[account.status]}</td>
    </tr>
  );
}

type Load = api.Tank | "missing" | "server" | "network" | "session" | undefined;

const SERVER_PROBLEM = "The server has a problem. Try again in a few minutes.";

function TankPage({
  id,
  base,
  org,
  auth,
  user,
}: {
  id: string;
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
}) {
  const [tank, setTank] = useState<Load>(undefined);
  const [attempt, setAttempt] = useState(0);
  const now = useClock();
  useTitle(tank && typeof tank === "object" ? `${tank.label} · ${org.name}` : `Container · ${org.name}`);
  useCrumb(tank && typeof tank === "object" ? tank.label : null);

  useEffect(() => {
    if (!isId(id)) {
      setTank("missing");
      return;
    }
    setTank(undefined);
    user
      .getIdToken()
      .then((token) => api.tank(token, id))
      .then((found) => {
        if (found.account_id) {
          console.info(`tank page: sensor ${found.id} goes to the Sensor section of account ${found.account_id}`);
          history.replaceState(null, "", `${base}/accounts/${found.account_id}#sensor`);
          dispatchEvent(new PopStateEvent("popstate"));
          return;
        }
        setTank(found);
      })
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        setTank(code === 404 ? "missing" : code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
  }, [id, base, user, attempt]);

  const loaded = tank !== undefined;
  useEffect(() => {
    if (loaded && document.activeElement === document.body) document.querySelector<HTMLElement>("main h1")?.focus();
  }, [loaded]);

  const back = (
    <p>
      <a href={`${base}/tanks`} onClick={go} className="back" data-testid="back-to-tanks">
        ← All container levels
      </a>
    </p>
  );
  if (tank === undefined) {
    return (
      <main>
        {back}
        <Skeleton shape="detail" />
      </main>
    );
  }
  if (tank === "missing") {
    return (
      <main data-testid="tank-missing">
        {back}
        <h1 tabIndex={-1}>Container not found</h1>
        <p>No container with this address is visible to you.</p>
      </main>
    );
  }
  if (tank === "session") {
    return (
      <main data-testid="session-ended">
        {back}
        <h1 tabIndex={-1}>Your session ended</h1>
        <button className="primary" onClick={() => signOut(auth)}>
          Sign in again
        </button>
      </main>
    );
  }
  if (tank === "server" || tank === "network") {
    return (
      <main data-testid="tank-failed">
        {back}
        <h1 tabIndex={-1}>Cannot load the container</h1>
        <p role="alert" className="error">
          {tank === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      </main>
    );
  }
  return (
    <main className="settings" data-testid="tank-page">
      {back}
      <h1 tabIndex={-1}>
        {tank.label}
        {!tank.in_field && <TestChip />}
      </h1>
      <TankPanel tank={tank} onTank={setTank} auth={auth} user={user} timeZone={org.time_zone} now={now} />
    </main>
  );
}

function TankRow({ base, tank, now, timeZone }: { base: string; tank: api.Tank; now: Date; timeZone: string }) {
  const state = tank.state;
  const kind = attention(tank, now);
  return (
    <tr data-testid="tank-row" className={kind === "stale" && tank.in_field ? "row-stale" : undefined}>
      <th scope="row">
        <a href={`${base}/tanks/${tank.id}`} onClick={go} data-testid="tank-link">
          {tank.label}
        </a>
        {!tank.in_field && <TestChip />}
      </th>
      <td data-testid="tank-level" data-label="Level">
        <span className={`level level-${state?.measurement.level_state ?? "none"}`}>{level(tank)}</span>
      </td>
      <td className="battery num" data-testid="tank-battery" data-label="Battery">
        {battery(tank)}
        {lowBattery(tank) && <span className="chip-low">⚠ Low</span>}
      </td>
      <td data-testid="tank-age" data-label="Last reading">
        {state ? (
          <>
            <time dateTime={state.observed_at}>{age(state.observed_at, now)}</time>
            {kind === "stale" && (
              <span className="chip-stale" data-testid="tank-stale">
                ⚠ Stale
              </span>
            )}
            <span className="when">{readingTime(state.observed_at, timeZone)}</span>
          </>
        ) : (
          <span className="chip-stale" data-testid="tank-stale">
            ⚠ Never reported
          </span>
        )}
      </td>
    </tr>
  );
}
